import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { TicketTypeEnum } from '../../../../../core/enums/ticket-type.enum';
import { TicketEquipmentCreateUpdateDeleteComponent } from '../modals/equipment/ticket-equipment-create-update-delete/ticket-equipment-create-update-delete.component';
import { TicketRequirementCreateUpdateDeleteComponent } from '../modals/requirement/ticket-requirement-create-update-delete/ticket-requirement-create-update-delete.component';
import { TicketService } from '../ticket.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { EstablishmentService } from '../../../management/establishment/establishment.service';
import { AuthService } from '../../../../../core/services/auth.service';
import { map } from 'rxjs/operators';
import { finalize, lastValueFrom } from 'rxjs';
import { TicketStatusEnum } from '../../../../../core/enums/ticketStatusEnum';
import { TicketModel } from '../../../../../core/models/ticket.model';
import { CrudModeEnum } from '../../../../../core/base/enum/crud-mode.enum';
import { EstablishmentModel } from '../../../../../core/models/establishment.model';
import { SnackbarService } from '../../../../../core/services/snackbar.service';
import { ENUM_PERMISSIONS } from '../../../../../core/enums/permission.enum';
import { HotTopicModel } from '../../../../../core/models/hot-topic.model';
import { HotTopicService } from '../../hot-topic/hot-topic.service';
import { quillConfigEmpty } from '../../../../../core/config/quill.config';

@Component({
  selector: 'vex-ticket-home',
  templateUrl: './ticket-home.component.html',
  styleUrls: [
    './ticket-home.component.scss',
    '../../../../../../../node_modules/quill/dist/quill.snow.css',
    '../../../../../../@vex/styles/partials/plugins/_quill.scss',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class TicketHomeComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  loadingEshopBtn = false;
  loadingEticketBtn = false;
  disabledEshopBtn = false;
  disabledEticketBtn = false;
  disabledDemandsBtn = false;
  isPredik = false;
  permissions = ENUM_PERMISSIONS;

  hotTopics: HotTopicModel[] = [];
  carouselItems: TemplateRef<any>[] = [];
  @ViewChildren('topicRef') topicTemplates: QueryList<TemplateRef<any>>;

  bannerUrl: string = 'assets/img/predik_tool_banner_desktop.svg';
  defaultBannerPhone = '../../../../../assets/img/predik_tool_banner_phone.svg';

  readonly quillConfig = quillConfigEmpty;

  constructor(
    public service: TicketService,
    private hotTopicService: HotTopicService,
    private dialog: MatDialog,
    private router: Router,
    private establishmentService: EstablishmentService,
    public authService: AuthService,
    private snackbarService: SnackbarService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.hotTopicService
      .findAll(null, null, 'createdAt', 'desc', '', { requiredFields: ['contract'], isActive: true })
      .subscribe((res: any) => {
        this.hotTopics = res.data;
      });

    this.checkPermission();

    // Refresh banner whenever currentUser changes (e.g. after a logo update).
    this.authService
      .currentUser$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        const tenantId = user?.currentTenant?._id;
        if (tenantId) {
          this.bannerUrl = `/api/customer-group/${tenantId}/banner?t=${Date.now()}`;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    Promise.resolve().then(() => {
      this.carouselItems = this.topicTemplates.toArray();
    });

    this.topicTemplates.changes.subscribe((list: QueryList<TemplateRef<any>>) => {
      Promise.resolve().then(() => {
        this.carouselItems = list.toArray();
      });
    });
  }

  async createItem(type: TicketTypeEnum) {
    if (type === TicketTypeEnum.REQUIREMENT) {
      this.loadingEshopBtn = true;
      this.disabledEticketBtn = true;

      const establishment = await this.getOneEstablishmentOrNull();

      if (establishment) {
        return this.submit(establishment);
      }

      return this.openCreateModal();
    }

    if (type === TicketTypeEnum.INCIDENT) {
      this.loadingEticketBtn = true;
      this.disabledEshopBtn = true;
      this.dialog
        .open(TicketEquipmentCreateUpdateDeleteComponent, {})
        .afterClosed()
        .subscribe(result => {
          if (result) {
            if (result?.data?._id) {
              this.router
                .navigate(['administrative/ticket', result?.data?._id, 'description-incident'], {
                  state: { skipTicketExistsCheck: true },
                })
                .then();
            }
          }
          this.loadingEshopBtn = false;
          this.disabledEticketBtn = false;
          this.disabledEshopBtn = false;
          this.loadingEticketBtn = false;
        });
    }
  }

  private openCreateModal(data?: { preselectedEstablishment?: any }): void {
    this.dialog
      .open(TicketRequirementCreateUpdateDeleteComponent, {
        data: {
          ...data,
          mode: CrudModeEnum.Create,
        },
        width: '640px',
        maxHeight: '90vh',
      })
      .afterClosed()
      .subscribe(result => {
        if (result?.data?._id) {
          this.router
            .navigate(['administrative/ticket', result.data._id, 'catalog'], {
              state: { skipTicketExistsCheck: true },
            })
            .then();
        }
        this.loadingEshopBtn = false;
        this.disabledEticketBtn = false;
        this.disabledEshopBtn = false;
      });
  }

  createRequirement() {
    this.createItem(TicketTypeEnum.REQUIREMENT);
  }

  createIncident() {
    this.createItem(TicketTypeEnum.INCIDENT);
  }

  goToTicketList() {
    this.router.navigate(['administrative/ticket']).then();
  }

  async getOneEstablishmentOrNull() {
    const user = await this.authService.currentUserValue();
    const establishmentIds = user.affectations.map(x => x.establishment._id);

    return lastValueFrom(
      this.establishmentService
        .findAllAsSelect(true, '_id', 'desc', '', { includeIds: establishmentIds })
        .pipe(map((res: any) => (res?.data.length === 1 ? res.data[0] : null))),
    );
  }

  submit(establishmentModel: EstablishmentModel) {
    const { _id, label, technicalIdentifier } = establishmentModel;
    const data = {
      establishment: _id,
      type: TicketTypeEnum.REQUIREMENT,
    };
    this.service
      .create(data)
      .pipe(
        finalize(() => {
          this.loadingEshopBtn = false;
          this.disabledEticketBtn = false;
          this.disabledEshopBtn = false;
        }),
      )
      .subscribe(response => {
        this.snackbarService.success('Ticket créé avec succès');
        this.router
          .navigate(['administrative/ticket', response?.data?._id, 'catalog'], {
            state: { skipTicketExistsCheck: true },
          })
          .then();
      });
  }

  checkPermission() {
    this.authService.getCurrentUserPermissions$().subscribe(permission => {
      this.isPredik = this.authService.checkCurrentIsPredik();

      // DS-PRED-1253

      // if (!permission.includes(ENUM_PERMISSIONS.CREATE_TICKET_REQUIREMENT)) {
      //     this.disabledEshopBtn = true;
      // }
      // // if (!permission.includes(ENUM_PERMISSIONS.CREATE_TICKET_INCIDENT)) {
      // //     this.disabledEticketBtn = true;
      // // }
      // if (!permission.includes(ENUM_PERMISSIONS.READ_TICKET_TICKET)) {
      //     this.disabledDemandsBtn = true;
      // }
    });
  }
}
